import { SOURCE_ATTRIBUTION_STORAGE_KEY } from '../shared/source-attribution.js';
import { isFunction, isNumber, isObject, isString } from '../shared/runtime-values.js';
export const ANALYTICS_OPT_OUT_STORAGE_KEY = 'drawonamap:analytics-opt-out:v1';
export const INTERNAL_ANALYTICS_EXCLUSION_STORAGE_KEY = 'drawonamap:internal-analytics-exclusion:v1';
export const ANALYTICS_VISITOR_STORAGE_KEY = 'drawonamap:analytics-visitor:v1';
export const ANALYTICS_VISITOR_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const VISITOR_ID_PATTERN = /^[a-f0-9-]{36}$/;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface CryptoLike {
  randomUUID?: () => string;
  getRandomValues<T extends ArrayBufferView>(array: T): T;
}

interface VisitorRecord {
  id: string;
  expires_at: number;
}

function browserStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function browserCrypto(): CryptoLike | null {
  // SAFETY: The local owner contract defines this value with the stated concrete type.
  return !('crypto' in globalThis) ? null : crypto as CryptoLike;
}

function createVisitorId(cryptoSource: CryptoLike): string {
  if (isFunction(cryptoSource.randomUUID)) {
    return cryptoSource.randomUUID().toLowerCase();
  }
  const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function isAnalyticsOptedOut(storage: StorageLike | null = browserStorage()): boolean {
  if ('window' in globalThis && window.location?.hostname === 'drawonamap.com'
    && 'navigator' in globalThis && navigator.webdriver) return true;
  if (!storage) return true;
  try {
    return storage.getItem(ANALYTICS_OPT_OUT_STORAGE_KEY) === 'true'
      || storage.getItem(INTERNAL_ANALYTICS_EXCLUSION_STORAGE_KEY) === 'true';
  } catch {
    return true;
  }
}

export function setInternalAnalyticsExclusion(
  excluded: boolean,
  storage: StorageLike | null = browserStorage(),
): void {
  if (!storage) return;
  try {
    if (excluded) {
      storage.setItem(INTERNAL_ANALYTICS_EXCLUSION_STORAGE_KEY, 'true');
      storage.removeItem(ANALYTICS_VISITOR_STORAGE_KEY);
      storage.removeItem(SOURCE_ATTRIBUTION_STORAGE_KEY);
      return;
    }
    storage.removeItem(INTERNAL_ANALYTICS_EXCLUSION_STORAGE_KEY);
  } catch {
    // Analytics preferences must never interrupt the page flow.
  }
}

export function setAnalyticsOptOut(
  optedOut: boolean,
  storage: StorageLike | null = browserStorage(),
): void {
  if (!storage) return;
  try {
    if (optedOut) {
      storage.setItem(ANALYTICS_OPT_OUT_STORAGE_KEY, 'true');
      storage.removeItem(ANALYTICS_VISITOR_STORAGE_KEY);
      storage.removeItem(SOURCE_ATTRIBUTION_STORAGE_KEY);
      return;
    }
    storage.removeItem(ANALYTICS_OPT_OUT_STORAGE_KEY);
  } catch {
    // Analytics preferences must never interrupt the page flow.
  }
}

export function getOrCreateAnalyticsVisitorId({
  storage = browserStorage(),
  cryptoSource = browserCrypto(),
  now = Date.now(),
}: {
  storage?: StorageLike | null;
  cryptoSource?: CryptoLike | null;
  now?: number;
} = {}): string | null {
  if (!storage || !cryptoSource || isAnalyticsOptedOut(storage)) return null;

  try {
    const raw = storage.getItem(ANALYTICS_VISITOR_STORAGE_KEY);
    if (raw) {
      try {
        const value: unknown = JSON.parse(raw);
        if (value && isObject(value)) {
          // SAFETY: The following parser checks every field that it accepts from this external payload.
          const record = value as Partial<VisitorRecord>;
          if (
            isString(record.id)
            && VISITOR_ID_PATTERN.test(record.id)
            && isNumber(record.expires_at)
            && Number.isFinite(record.expires_at)
            && record.expires_at > now
          ) return record.id;
        }
      } catch {
        // A corrupt record is missing identity state. Replace it below.
      }
      storage.removeItem(ANALYTICS_VISITOR_STORAGE_KEY);
    }

    const record: VisitorRecord = {
      id: createVisitorId(cryptoSource),
      expires_at: now + ANALYTICS_VISITOR_TTL_MS,
    };
    storage.setItem(ANALYTICS_VISITOR_STORAGE_KEY, JSON.stringify(record));
    return record.id;
  } catch {
    return null;
  }
}
